// frontend/admin-portal/src/components/procurement/RequisitionItemEditor.jsx
import React, { useEffect, useState } from "react";
import { Button, Input } from "@fiberhome/shared-ui";
import { productService } from "../../services/productService";
import { MarginBadge, computeMargin } from "@fiberhome/shared-ui";

/**
 * Editable list of items for a requisition.
 * Each item: { productId?, freeTextName?, quantity, unit, targetPrice?, notes? }
 */
export default function RequisitionItemEditor({ items = [], onChange }) {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    if (!showPicker) return;
    let alive = true;
    productService
      .list({ search, limit: 30, isActive: true })
      .then(({ items }) => alive && setProducts(items));
    return () => {
      alive = false;
    };
  }, [search, showPicker]);

  const addProductItem = (p) => {
    onChange([
      ...items,
      {
        productId: p.id,
        freeTextName: null,
        quantity: 1,
        unit: p.unit || "pcs",
        targetPrice: p.costPrice || "",
        notes: "",
      },
    ]);
    setShowPicker(false);
    setSearch("");
  };

  const addFreeText = () => {
    onChange([
      ...items,
      {
        productId: null,
        freeTextName: "",
        freeTextSpec: "",
        quantity: 1,
        unit: "pcs",
        targetPrice: "",
        notes: "",
      },
    ]);
  };

  const update = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div>
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <Button
          size="sm"
          variant="primary"
          onClick={() => setShowPicker((v) => !v)}
        >
          + Add from catalog
        </Button>
        <Button size="sm" variant="outline" onClick={addFreeText}>
          + Add free-text item
        </Button>
      </div>

      {showPicker && (
        <div className="fh-prod-picker mb-3">
          <Input
            placeholder="Search catalog..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="fh-prod-picker__list">
            {products.map((p) => (
              <button
                key={p.id}
                type="button"
                className="fh-prod-picker__item"
                onClick={() => addProductItem(p)}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{p.name}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {p.sku} {p.category ? `· ${p.category.name}` : ""}
                  </div>
                </div>
                <span className="text-muted" style={{ fontSize: 12 }}>
                  stock: {p.stock} {p.unit}
                </span>
              </button>
            ))}
            {products.length === 0 && (
              <div className="text-muted p-3" style={{ fontSize: 13 }}>
                No products
              </div>
            )}
          </div>
        </div>
      )}

      {items.length === 0 && (
        <div
          className="text-muted p-4 text-center"
          style={{ border: "1px dashed var(--fh-border)", borderRadius: 10 }}
        >
          No items yet. Add products from the catalog or a free-text item.
        </div>
      )}

      {items.length > 0 && (
        <div className="fh-req-items">
          {items.map((it, idx) => (
            <div className="fh-req-item" key={idx}>
              <div className="fh-req-item__main">
                {it.productId ? (
                  <>
                    <div style={{ fontWeight: 600 }}>
                      {it.productName || `Product #${it.productId}`}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {it.productSku || ""}
                    </div>
                  </>
                ) : (
                  <>
                    <Input
                      placeholder="Item name (not in catalog)"
                      value={it.freeTextName || ""}
                      onChange={(e) =>
                        update(idx, { freeTextName: e.target.value })
                      }
                    />
                    <Input
                      placeholder="Specification / details (optional)"
                      value={it.freeTextSpec || ""}
                      onChange={(e) =>
                        update(idx, { freeTextSpec: e.target.value })
                      }
                    />
                  </>
                )}
              </div>

              <div className="fh-req-item__qty">
                <Input
                  label="Qty"
                  type="number"
                  min={1}
                  value={it.quantity}
                  onChange={(e) =>
                    update(idx, { quantity: Number(e.target.value) })
                  }
                />
              </div>

              <div className="fh-req-item__unit">
                <Input
                  label="Unit"
                  value={it.unit}
                  onChange={(e) => update(idx, { unit: e.target.value })}
                />
              </div>

              <div className="fh-req-item__price">
                <Input
                  label="Target price"
                  type="number"
                  value={it.targetPrice}
                  onChange={(e) => update(idx, { targetPrice: e.target.value })}
                />
              </div>

              

              <div className="fh-req-item__notes">
                <Input
                  label="Notes"
                  value={it.notes || ""}
                  onChange={(e) => update(idx, { notes: e.target.value })}
                />
              </div>

              <div className="fh-req-item__remove">
                <Button size="sm" variant="ghost" onClick={() => remove(idx)}>
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
